import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Trash2, ArrowRight, Plus, AtSign, Link2, Tag, Copy, GripVertical, ScanText, Replace as ReplaceIcon } from "lucide-react";
import { RuleTemplates } from "./rules/RuleTemplates";
import {
  DataTable, EmptyState, FilterSelect, IconBtn, PageHead, PageShell, Pagination,
  Pill, Row, SearchInput, Td, Toolbar, Truncate, usePagedRows,
} from "@/components/ui/kit";

const catMeta = {
  username: { icon: AtSign, color: "var(--c-sky)", label: "Username" },
  url: { icon: Link2, color: "var(--c-teal)", label: "URL" },
  brand: { icon: Tag, color: "var(--c-amber)", label: "Brand" },
  ocr_skip: { icon: ScanText, color: "var(--c-amber)", label: "OCR Skip" },
};

const COLUMNS = [
  { key: "find", label: "Find" },
  { key: "replace", label: "Replace with" },
  { key: "type", label: "Type", hide: "hidden md:table-cell" },
  { key: "status", label: "Status" },
  { key: "actions", label: "Actions", align: "right" },
];

export default function Rules() {
  const [rules, setRules] = useState([]);
  const [form, setForm] = useState({ old_value: "", new_value: "", category: "brand" });
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  const load = async () => {
    const { data } = await api.get("/rules");
    setRules(data);
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.old_value) return toast.error("Enter the value to find");
    await api.post("/rules", { ...form, enabled: true });
    setForm({ old_value: "", new_value: "", category: form.category });
    toast.success("Rule added");
    load();
  };

  const toggle = async (r) => {
    await api.put(`/rules/${r.id}`, { enabled: !r.enabled });
    load();
  };

  const remove = async (id) => {
    await api.delete(`/rules/${id}`);
    load();
  };

  const duplicate = async (rule) => {
    try {
      await api.post("/rules", {
        old_value: rule.old_value,
        new_value: rule.new_value,
        category: rule.category,
        enabled: rule.enabled,
      });
      toast.success("Rule duplicated");
      load();
    } catch (error) {
      toast.error("Could not duplicate rule");
    }
  };

  const startRuleDrag = (event, rule) => {
    event.dataTransfer.effectAllowed = "copy";
    event.dataTransfer.setData("application/x-clonecast-rule", JSON.stringify({
      id: rule.id,
      old_value: rule.old_value,
      new_value: rule.new_value,
      category: rule.category || "brand",
    }));
    event.dataTransfer.setData("text/plain", `${rule.old_value} → ${rule.new_value || "(removed)"}`);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = rules.filter((r) => {
      if (typeFilter !== "all" && (r.category || "brand") !== typeFilter) return false;
      if (statusFilter === "enabled" && !r.enabled) return false;
      if (statusFilter === "disabled" && r.enabled) return false;
      if (!q) return true;
      return [r.old_value, r.new_value].filter(Boolean).some((s) => String(s).toLowerCase().includes(q));
    });
    const sorters = {
      newest: (a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")),
      find: (a, b) => String(a.old_value).localeCompare(String(b.old_value)),
      type: (a, b) => String(a.category || "brand").localeCompare(String(b.category || "brand")),
    };
    return [...list].sort(sorters[sortBy] || sorters.newest);
  }, [rules, search, typeFilter, statusFilter, sortBy]);

  useEffect(() => { setPage(0); }, [search, typeFilter, statusFilter, sortBy, pageSize]);
  const paged = usePagedRows(filtered, { page, pageSize });
  const enabledCount = rules.filter((r) => r.enabled).length;

  return (
    <PageShell>
      {/* compact horizontal composer: Find → Replace → Type → Add */}
      <Card className="bg-[var(--tg-surface)] border-[var(--tg-border)] p-4">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_170px_auto] gap-3 items-end">
          <div className="space-y-1.5">
            <label className="label-caps">Find (old value)</label>
            <Input
              data-testid="input-old-value"
              value={form.old_value}
              onChange={(e) => setForm({ ...form, old_value: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && add()}
              placeholder="@oldchannel"
              className="bg-[var(--tg-bg)] border-[var(--tg-border)] h-9 focus:border-[var(--c-sky)] font-mono"
            />
          </div>
          <ArrowRight className="h-4 w-4 text-[var(--tg-faint)] mb-2.5 hidden md:block" />
          <div className="space-y-1.5">
            <label className="label-caps">{form.category === "ocr_skip" ? "Action" : "Replace with (new)"}</label>
            <Input
              data-testid="input-new-value"
              value={form.new_value}
              onChange={(e) => setForm({ ...form, new_value: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && add()}
              placeholder={form.category === "ocr_skip" ? "Skip entire image post" : "@mychannel"}
              disabled={form.category === "ocr_skip"}
              className="bg-[var(--tg-bg)] border-[var(--tg-border)] h-9 focus:border-[var(--c-sky)] font-mono"
            />
          </div>
          <div className="space-y-1.5">
            <label className="label-caps">Type</label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="select-rule-category" className="bg-[var(--tg-bg)] border-[var(--tg-border)] h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[var(--tg-elevated)] border-[var(--tg-border)] text-[var(--tg-strong)]">
                <SelectItem value="username">Username</SelectItem>
                <SelectItem value="url">URL</SelectItem>
                <SelectItem value="brand">Brand</SelectItem>
                <SelectItem value="ocr_skip">OCR Image Skip</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={add} className="h-9 bg-[var(--c-sky)] hover:bg-[var(--c-sky-hover)] text-white font-semibold" data-testid="add-rule-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Add
          </Button>
        </div>
      </Card>

      <PageHead
        title="Active Rules"
        count={rules.length}
        chips={
          <>
            <Pill tone="var(--c-emerald)">{enabledCount} enabled</Pill>
            {rules.length - enabledCount > 0 && <Pill tone="var(--tg-faint)">{rules.length - enabledCount} off</Pill>}
            {rules.length > 0 && (
              <span className="text-[10px] text-[var(--tg-faint)]">Drag a row onto a template to add it</span>
            )}
          </>
        }
      />

      <Toolbar>
        <SearchInput value={search} onChange={setSearch} placeholder="Search find / replace values…" testid="rules-search" />
        <FilterSelect
          value={typeFilter} onValueChange={setTypeFilter} testid="rules-type-filter"
          options={[
            { value: "all", label: "All types" },
            { value: "username", label: "Username" },
            { value: "url", label: "URL" },
            { value: "brand", label: "Brand" },
            { value: "ocr_skip", label: "OCR Skip" },
          ]}
        />
        <FilterSelect
          value={statusFilter} onValueChange={setStatusFilter} width="w-32" testid="rules-status-filter"
          options={[
            { value: "all", label: "All status" },
            { value: "enabled", label: "Enabled" },
            { value: "disabled", label: "Disabled" },
          ]}
        />
        <FilterSelect
          value={sortBy} onValueChange={setSortBy} testid="rules-sort"
          options={[
            { value: "newest", label: "Sort: newest" },
            { value: "find", label: "Sort: find" },
            { value: "type", label: "Sort: type" },
          ]}
        />
      </Toolbar>

      <DataTable
        columns={COLUMNS}
        testid="rules-table"
        minWidth="min-w-[640px]"
        empty={
          paged.slice.length === 0 ? (
            <EmptyState
              icon={ReplaceIcon}
              testid="no-rules"
              title={rules.length === 0 ? "No replacement rules" : "No rules match these filters"}
              hint={rules.length === 0 ? "Text is copied unchanged until you add a rule above." : undefined}
            />
          ) : null
        }
      >
        {paged.slice.map((r) => {
          const meta = catMeta[r.category] || catMeta.brand;
          const Icon = meta.icon;
          return (
            <Row key={r.id} testid={`rule-${r.id}`} tone={!r.enabled ? "var(--tg-faint)" : undefined}>
              <Td>
                <div
                  draggable
                  onDragStart={(event) => startRuleDrag(event, r)}
                  className="flex items-center gap-2 min-w-0 cursor-grab active:cursor-grabbing"
                  title="Drag onto a template to add it"
                >
                  <GripVertical className="h-3.5 w-3.5 text-[var(--tg-dim)] shrink-0" aria-label="Drag rule to a template" />
                  <span className="font-mono text-xs px-2 py-1 rounded bg-[rgb(var(--c-rose-rgb)/0.10)] text-[var(--c-rose)] truncate max-w-[240px]" title={r.old_value}>
                    {r.old_value}
                  </span>
                </div>
              </Td>
              <Td>
                <span className="font-mono text-xs px-2 py-1 rounded bg-[rgb(var(--c-emerald-rgb)/0.10)] text-[var(--c-emerald)] truncate max-w-[240px] inline-block align-middle"
                      title={r.category === "ocr_skip" ? "Skip image post" : (r.new_value || "(removed)")}>
                  {r.category === "ocr_skip" ? "Skip image post" : (r.new_value || "(removed)")}
                </span>
              </Td>
              <Td className="hidden md:table-cell">
                <Pill tone={meta.color} size="sm">
                  <Icon className="h-2.5 w-2.5" /> {meta.label}
                </Pill>
              </Td>
              <Td>
                <div className="flex items-center gap-2">
                  <Switch checked={r.enabled} onCheckedChange={() => toggle(r)} data-testid={`toggle-rule-${r.id}`} />
                  <span className="text-[11px] text-[var(--tg-faint)]">{r.enabled ? "On" : "Off"}</span>
                </div>
              </Td>
              <Td align="right">
                <div className="flex items-center justify-end gap-1">
                  <IconBtn icon={Copy} title="Duplicate rule" tone="var(--c-violet)" onClick={() => duplicate(r)} testid={`duplicate-rule-${r.id}`} />
                  <IconBtn icon={Trash2} title="Delete rule" tone="var(--c-rose)" onClick={() => remove(r.id)} testid={`delete-rule-${r.id}`} />
                </div>
              </Td>
            </Row>
          );
        })}
      </DataTable>

      {filtered.length > pageSize && (
        <Pagination
          page={paged.safePage} pageCount={paged.pageCount} total={paged.total}
          from={paged.from} to={paged.to} pageSize={pageSize} onPageSize={setPageSize}
          onPrev={() => setPage((p) => Math.max(0, p - 1))}
          onNext={() => setPage((p) => Math.min(paged.pageCount - 1, p + 1))}
          testid="rules-pagination"
        />
      )}

      <div className="pt-1 border-t border-[var(--tg-border)]">
        <RuleTemplates />
      </div>
    </PageShell>
  );
}
